import {
  styled,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  Divider,
  DialogContent,
  DialogActions,
  TablePagination,
  TableContainer,
} from '@mui/material';
import * as React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { GetImportsResponse, useGetFeatureAnalysesListQuery, useGetImportListQuery, useStartFeatureAnalysesImportMutation } from 'src/redux/services/imports';
import { Import } from 'src/dto/Import';
import ButtonComponent from 'src/components/ButtonComponent';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import DragAndDropFiles from 'src/components/DragAndDropFiles/DragAndDropFiles';
import Row from 'src/frontend-common/components/Row';
import LoadingIcon from 'src/frontend-common/components/Icons/Loading';
import { BASE_URL } from 'src/redux/services/utils';

import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Column from 'src/frontend-common/components/Column';
import { Upload } from '@mui/icons-material';
import { ExportHeaderRow } from 'src/style/common';
import EmptyState from 'src/frontend-common/components/EmptyState';
import AppkeyAutocomplete from 'src/components/AppkeyAutocomplete';
import Chip from 'src/frontend-common/components/Chip';
import { getAccessToken } from 'src/frontend-common/localstorage';
const TableRowWrapper = styled(TableRow)(() => ({
  '&:hover': {
    backgroundColor: '#F3F4F6',
  },
}));

// eslint-disable-next-line complexity
const Insights: React.FC = () => {
  const [openModal, setOpenModal] = React.useState<boolean>(false);

  const [page, setPage] = React.useState(0);
  const [dataTable, setDataTable] = React.useState<GetImportsResponse>();
  const [validatingFile, setValidatingFile] = React.useState<boolean>(true);
  const [validatedFileID, setValidatedFileID] = React.useState<string | undefined>(undefined);
  const [selectedAppkey, setSelectedAppkey] = React.useState<{
    label: string;
    key: string;
  } | null>(null);
  const [showSpinner, setShowSpinner] = React.useState<boolean>(false);

  const tableCells = ['File Name', 'Status', 'Records', 'Errors', 'User', 'Date', ' '];

  const { data: importData, isLoading: isLoadingImportData } = useGetImportListQuery('', { pollingInterval: 3000 });

  const { data: featureAnalysesImportData } = useGetFeatureAnalysesListQuery('', { pollingInterval: 3000 });

  const [startFeatureAnalysesImport] = useStartFeatureAnalysesImportMutation();
  const navigate = useNavigate();

  React.useEffect(() => {
    setDataTable(featureAnalysesImportData);
  }, [featureAnalysesImportData, importData]);

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleChangePage = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const token = getAccessToken();
  const handleChangeRowsPerPage = () => {
    setPage(0);
  };
  const rowsPerPage = 10;

  const importFile = () => {
    startFeatureAnalysesImport(validatedFileID);

    setOpenModal(false);
    setShowSpinner(true);

    setTimeout(() => setShowSpinner(false), 4000);
  };

  const doFetchDownload = () => {
    fetch(`${BASE_URL}/v1/feature-analyses/example`, {
      headers: { Authorization: token || '' },
    })
      .then((resp) => resp.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        // the filename you want
        a.download = 'example.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => console.log('error'));
  };

  if (importData?.documents) {
    return (
      <DashboardPageLayout
        title="Insights"
        aside={
          <ButtonComponent
            buttontype="mainaction"
            text="Upload"
            startIcon={<Upload />}
            disabled={showSpinner}
            onClick={() => {
              setOpenModal(true);
              setValidatingFile(true);
            }}
          />
        }
      >
        <Dialog fullWidth={true} maxWidth="sm" open={openModal} onClose={handleClose}>
          <ExportHeaderRow>
            <Typography variant="h4">Upload</Typography>
            <ButtonComponent icon={<CloseIcon />} buttontype="icon" onClick={handleClose} />
          </ExportHeaderRow>

          <DialogContent style={{ overflow: 'hidden', paddingTop: 25 }}>
            <Typography variant="h4">App key</Typography>
            <AppkeyAutocomplete selectedAppkey={selectedAppkey} setSelectedAppkey={setSelectedAppkey} sx={{ marginBottom: '16px' }} activeOnly={true} />
            <Typography variant="h5" style={{ marginBottom: 10 }}>
              How to upload
            </Typography>
            <Typography color="text.secondary">
              1. Download our example{' '}
              <span style={{ color: 'blue', cursor: 'pointer' }} onClick={doFetchDownload}>
                CSV file.
              </span>
            </Typography>
            <Typography color="text.secondary">2. Add your data to our template and make sure to export or save as a .csv file.</Typography>
            <Typography color="text.secondary">3. Upload it below, for processing.</Typography>
            <DragAndDropFiles
              setValidatingFile={setValidatingFile}
              setValidatedFileID={setValidatedFileID}
              isFeatureAnalyses={true}
              appkey={selectedAppkey?.key || ''}
            />
            <Divider light />
            <DialogActions>
              <ButtonComponent buttontype="cancel" onClick={handleClose} style={{ marginRight: 16 }} />

              <ButtonComponent buttontype="mainaction" text="Import" onClick={importFile} disabled={validatingFile} />
            </DialogActions>{' '}
          </DialogContent>
        </Dialog>

        {!isLoadingImportData && dataTable?.documents.length === 0 ? (
          <Column>
            <Box style={{ position: 'relative', backgroundColor: 'white' }}>
              <EmptyState style={{ width: '100%', padding: '80px 20px' }} />
            </Box>
          </Column>
        ) : (
          <Box>
            {showSpinner ? (
              <div style={{ textAlign: 'center', paddingTop: '100px' }}>
                <LoadingIcon />
              </div>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {tableCells.map((tableCell) => (
                          <TableCell align="left" key={tableCell}>
                            <Row>
                              <>{tableCell}</>
                            </Row>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody style={{ backgroundColor: 'white' }}>
                      {dataTable?.documents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item: Import, i: number) => {
                        const progressValue = (item.number_of_processed_rows || 0) / ((item.number_of_retry || 0) + (item.number_of_rows || 1));

                        return (
                          <TableRowWrapper key={`${item.key}-${item.created_at}-${item.import_status}`}>
                            <TableCell align="left">{item.original_file_name}</TableCell>
                            <TableCell align="left">
                              <Chip variant="progress" progressPerc={progressValue * 100} />
                            </TableCell>

                            <TableCell align="left">{item.number_of_rows}</TableCell>
                            <TableCell align="left">{item.number_of_errors}</TableCell>
                            <TableCell align="left">{item.author}</TableCell>
                            <TableCell align="left">{item?.created_at ? format(new Date(item.created_at), 'yyyy/MM/dd | hh:mm') : ''}</TableCell>

                            <TableCell key={`action-${i}`}>
                              <ButtonComponent
                                icon={<SearchIcon />}
                                buttontype="icon"
                                disabled={
                                  item.import_status !== 'COMPLETED' ||
                                  (item.import_status === 'COMPLETED' &&
                                    (item.validation_errors > 0 || item.number_of_errors > 0 || item.processing_errors > 0))
                                }
                                onClick={() => {
                                  navigate(`/feature-details/${item.key}`);
                                }}
                              />
                            </TableCell>
                          </TableRowWrapper>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  style={{ backgroundColor: 'white' }}
                  count={dataTable?.documents.length || 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[rowsPerPage]}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Box>
        )}
      </DashboardPageLayout>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: '25%' }}>
      <LoadingIcon />
    </div>
  );
};

export default Insights;
